import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reportsApi } from '../services/reportsApi';

// Fallback mock data for modules NOT connected yet (Monitoring, Brand Protection)
import { monitoringAlerts, brandProtection as mockBP } from '../mocks/reportsData';

export const useReportsData = (filters) => {
    const { token } = useAuth();
    const [data, setData] = useState({
        clients: [],
        trademarks: [],
        alerts: [],
        procedures: [],
        brandProtection: [],
        loading: true,
        error: null
    });

    useEffect(() => {
        let mounted = true;

        const fetchData = async () => {
            if (!token) return;

            setData(prev => ({ ...prev, loading: true, error: null }));

            try {
                // 1. Fetch Real Data
                let [clientsData, trademarksData, proceduresData] = await Promise.all([
                    reportsApi.getClients(token),
                    reportsApi.getTrademarks(token, filters),
                    reportsApi.getProcedures(token, filters)
                ]);

                // Defensive checks for arrays
                clientsData = Array.isArray(clientsData) ? clientsData : [];
                trademarksData = Array.isArray(trademarksData) ? trademarksData : [];
                proceduresData = Array.isArray(proceduresData) ? proceduresData : [];

                // 2. Process & Enrich Data (Frontend Logic)

                // --- Trademarks Processing ---
                const processedTrademarks = trademarksData.map(tm => {
                    // Calculate Risk (Temporary Logic)
                    // High: Vencida, Cancelada, Negada OR vigencia < 6 months
                    // Medium: En trámite > 1 year
                    // Low: Registrada and vigencia > 1 year

                    let risk = 'low';
                    const now = new Date();
                    let vigenciaDate = null;

                    if (tm.fecha_vigencia) {
                        vigenciaDate = new Date(tm.fecha_vigencia);
                        const monthsUntilExp = (vigenciaDate - now) / (1000 * 60 * 60 * 24 * 30);
                        if (monthsUntilExp <= 6) risk = 'high';
                        else if (monthsUntilExp <= 12) risk = 'medium';
                    }

                    // Estatus based risk
                    // Since we don't have exact status names from ID easily without catalog, 
                    // we rely on 'estatus_interno' if it matches known IDs or if we fetch catalog.
                    // For now, let's use a simpler heuristic if available or keep it basic.
                    // We'll map backend fields to frontend expected fields
                    return {
                        ...tm,
                        name: tm.denominacion,
                        status: tm.estatus_impi_desc || tm.estatus_interno || 'Desconocido', // Fallback
                        class: tm.clase ? tm.clase.join(', ') : '',
                        vigencia: tm.fecha_vigencia ? new Date(tm.fecha_vigencia).toLocaleDateString('es-MX') : '-',
                        risk,
                        clientId: tm.client_id,
                        nextAction: risk === 'high' ? 'Renovación' : 'Vigilancia', // Dummy logic based on risk
                        nextActionDate: vigenciaDate ? vigenciaDate.toLocaleDateString('es-MX') : '-'
                    };
                });

                // --- Procedures Processing ---
                const processedProcedures = proceduresData.map(proc => {
                    let risk = 'low';
                    if (proc.deadline_date) {
                        const deadline = new Date(proc.deadline_date);
                        const daysUntil = (deadline - now) / (1000 * 60 * 60 * 24);
                        if (daysUntil < 30) risk = 'high';
                        else if (daysUntil < 90) risk = 'medium';
                    }

                    return {
                        ...proc,
                        trademark: proc.trademark_name || proc.trademark_id || 'N/A', // If backend sends name
                        type: proc.procedure_type,
                        date: proc.deadline_date ? new Date(proc.deadline_date).toLocaleDateString('es-MX') : '-',
                        risk,
                        clientId: proc.client_id
                    };
                });

                // --- Filtering Logic (Client Side for Period/Specifics) ---
                let filteredTM = processedTrademarks;
                let filteredProc = processedProcedures;

                // Filter by Period (Creation or Vigencia)
                if (filters.period && filters.period !== 'all') {
                    // TODO: Implement date range filtering if needed
                    // For now, assume period filters based on 'created_at' or 'deadline'
                }

                // Filter by Risk
                if (filters.risk) {
                    filteredTM = filteredTM.filter(t => t.risk === filters.risk);
                    filteredProc = filteredProc.filter(p => p.risk === filters.risk);
                }

                // --- Mock Data Mix-in (Monitoring/BP) ---
                // We filter mocks by client just to be consistent
                let filteredAlerts = [...monitoringAlerts];
                let filteredBP = [...mockBP];
                if (filters.client && filters.client !== 'all') {
                    filteredAlerts = filteredAlerts.filter(a => a.clientId === filters.client);
                    filteredBP = filteredBP.filter(b => b.clientId === filters.client);
                }

                if (mounted) {
                    setData({
                        clients: clientsData.map(c => ({ id: c.id, name: c.nombre })), // Map to expected shape
                        trademarks: filteredTM,
                        alertas: filteredAlerts, // Legacy name check? Hook returns 'alerts'
                        alerts: filteredAlerts,
                        procedures: filteredProc,
                        brandProtection: filteredBP,
                        loading: false,
                        error: null
                    });
                }
            } catch (err) {
                console.error("Reports API Error:", err);
                if (mounted) {
                    setData(prev => ({
                        ...prev,
                        loading: false,
                        error: "Error al cargar datos del reporte."
                    }));
                }
            }
        };

        fetchData();

        return () => { mounted = false; };
    }, [token, filters.client, filters.period, filters.risk]); // Re-fetch when token or major filters change

    return data;
};
